import { ReactNode, useCallback } from 'react';

import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button } from '@/components/ui/button';
import useLocalStorage from '@/hooks/useLocalStorage';
import { selectIframeView } from '@/redux/user/selectors';

interface IntegrationsContextualHelpProps {
  children: ReactNode;
  title: string;
  name: string;
}

/**
 * IntegrationsContextualHelp - A specialized contextual help component for the Integrations page
 *
 * Displays a dismissible help box with custom styling to match the integrations page design.
 *
 * @param children - The content to display in the help box
 * @param title - The title displayed at the top of the help box
 * @param name - Unique identifier for localStorage persistence
 * @returns The contextual help component or null if dismissed/hidden
 */
const IntegrationsContextualHelp = ({ children, name, title }: IntegrationsContextualHelpProps) => {
  const [localDismiss, setLocalDismiss] = useLocalStorage(`banners.${name}`, false);
  const isIframeView = useSelector(selectIframeView);
  const { t } = useTranslation();

  const handleDismiss = useCallback(() => {
    setLocalDismiss(true);
  }, [setLocalDismiss]);

  if (localDismiss || isIframeView) {
    return null;
  }

  return (
    <div className="relative mb-8 rounded-md bg-bg-info-soft p-4">
      <h3 className="mb-2 font-medium">{title}</h3>
      <div className="pr-8 text-sm leading-6">{children}</div>
      <div className="absolute top-3 right-3">
        <Button
          onClick={handleDismiss}
          aria-label={t('common.close_help')}
          variant="ghost-neutral"
          size="icon-xss"
        >
          <XIcon size={16} className="text-foreground-muted" />
        </Button>
      </div>
    </div>
  );
};

export default IntegrationsContextualHelp;
